import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { fmtDate } from "../../../../lib/misc";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Panel from "../../components/Panel";
import Plate from "../../components/Plate";
import CompactStat from "./CompactStat";
import StatusBadge from "./StatusBadge";
import BookingFilters from "./BookingFilters";
import BookingRowAction, { statusNote } from "./BookingRowAction";

/* ---------- Expanded lot inventory (stats, fill bar, bookings on lot) ---------- */

const useStyles = makeStyles({
  panel: { marginTop: 12, padding: 20 },
  head: { display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  title: { fontSize: 18, fontWeight: 600, color: T.fg },
  distance: { marginTop: 2, fontSize: 12, color: T.mutedFg },
  closeBtn: { border: "none", background: "transparent", fontSize: 13, color: T.mutedFg, cursor: "pointer", fontFamily: "inherit" },
  statGrid: {
    marginTop: 16,
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(110px, 1fr))",
    gap: 12,
    "@media (max-width: 980px)": { gridTemplateColumns: "repeat(3, 1fr)" },
    "@media (max-width: 640px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  },
  track: { marginTop: 16, height: 8, width: "100%", overflow: "hidden", borderRadius: 999, background: T.muted },
  bar: { height: "100%", borderRadius: 999 },
  fillNote: { marginTop: 6, fontSize: 11, color: T.mutedFg },
  listHead: { margin: "20px 0 8px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: T.subtleFg },
  empty: { borderRadius: 8, border: `1px dashed ${T.border}`, background: T.surface, padding: "16px 12px", fontSize: 13, color: T.mutedFg },
  filters: { marginBottom: 16 },
  tableWrap: {
    overflow: "auto",
    maxHeight: 520,
    borderRadius: 8,
    border: `1px solid ${T.borderSoft}`,
    padding: "8px 8px 4px",
  },
  table: { width: "100%", minWidth: 980, borderCollapse: "collapse", tableLayout: "fixed" },
  th: { textAlign: "left", fontSize: 12, fontWeight: 600, color: T.mutedFg, padding: "5px 10px", borderBottom: `0.5px solid ${T.borderSoft}`, letterSpacing: "0.04em", whiteSpace: "nowrap" },
  thRight: { textAlign: "right" },
  row: { borderBottom: `0.5px solid ${T.borderSoft}`, cursor: "pointer" },
  td: { padding: "8px 10px", fontSize: 13, fontWeight: 500, color: T.fg, verticalAlign: "middle" },
  tdRight: { textAlign: "right" },
  mono: { fontFamily: "monospace" },
  muted: { color: T.mutedFg },
  italicMuted: { fontStyle: "italic", color: T.subtleFg },
  statusCell: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 },
  statusNote: { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: T.mutedFg },
  showMoreWrap: { display: "flex", justifyContent: "center", marginTop: 12 },
});

const VISIBLE_BOOKINGS_LIMIT = 5;

const TICKET_STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "awaiting", label: "Awaiting" },
  { id: "allocated", label: "Allocated" },
  { id: "redeemed", label: "Redeemed" },
  { id: "flagged", label: "Flagged" },
];

export default function LotInventoryPanel({ lot, bookings, timezone, onClose, onOpenBooking, onTakePayment, onAssign, onValidate }) {
  const s = useStyles();
  const ui = useUi();
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const remaining = lot.available == null ? "-" : lot.available;
  const pct = lot.occupancyPercent == null ? 0 : Math.min(100, lot.occupancyPercent);
  const upcoming = [...bookings].sort((a, b) => String(a.startDay || "").localeCompare(String(b.startDay || "")));
  const filteredBookings = upcoming
    .filter((booking) => statusFilter === "all" || booking.status === statusFilter)
    .filter((booking) => {
      const value = query.trim().toLowerCase();

      if (!value) return true;

      return [
        booking.ref,
        booking.guestName,
        booking.email,
        booking.phone,
        booking.plate,
        booking.voucherId,
        booking.status,
        booking.booking?.id,
      ].some((field) => String(field || "").toLowerCase().includes(value));
    });
  const visibleBookings = showAllBookings ? filteredBookings : filteredBookings.slice(0, VISIBLE_BOOKINGS_LIMIT);
  const shouldShowBookingsToggle = filteredBookings.length > VISIBLE_BOOKINGS_LIMIT;
  const meta = [
    lot.type === "OFFSITE" ? "Off-site" : "On-site",
    lot.walkingDistanceMinutes != null ? `${lot.walkingDistanceMinutes} min walk` : null,
    lot.address,
  ].filter(Boolean).join(" - ");

  useEffect(() => {
    setShowAllBookings(false);
    setQuery("");
    setStatusFilter("all");
  }, [lot.id]);

  useEffect(() => {
    setShowAllBookings(false);
  }, [query, statusFilter]);

  return (
    <Panel className={s.panel}>
      <div className={s.head}>
        <div>
          <div className={s.title}>{lot.name}</div>
          <div className={s.distance}>{meta || lot.description}</div>
        </div>
        <button onClick={onClose} className={s.closeBtn}>Close x</button>
      </div>

      <div className={s.statGrid}>
        <CompactStat label="Capacity" value={lot.capacity == null ? "-" : lot.capacity} />
        <CompactStat label="Occupied" value={lot.occupied} />
        <CompactStat label="Allocated" value={lot.allocatedTickets} />
        <CompactStat label="Available" value={remaining} tone={lot.available <= 10 ? "warning" : "default"} />
        <CompactStat label="Redeemed tickets" value={lot.redeemedTickets} />
        <CompactStat label="Flagged tickets" value={lot.flaggedTickets} tone={lot.flaggedTickets > 0 ? "warning" : "default"} />
      </div>

      <div className={s.track}>
        <div className={s.bar} style={{ width: `${pct}%`, background: pct > 85 ? T.warningSolid : T.primary }} />
      </div>
      <div className={s.fillNote}>{pct}% occupancy</div>

      <div className={s.listHead}>Bookings on this lot ({upcoming.length})</div>
      {upcoming.length === 0 ? (
        <div className={s.empty}>No bookings yet on this lot.</div>
      ) : (
        <>
          <div className={s.filters}>
            <BookingFilters
              query={query}
              onQuery={setQuery}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
              pills={TICKET_STATUS_FILTERS}
              placeholder="Search this lot by booking, ticket, plate, guest or email..."
            />
          </div>
          {filteredBookings.length === 0 ? (
            <div className={s.empty}>No bookings match those filters.</div>
          ) : (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.th}>Plate</th>
                    <th className={s.th}>Guest</th>
                    <th className={s.th}>Start</th>
                    <th className={s.th}>End</th>
                    <th className={s.th}>Card</th>
                    <th className={s.th}>Status</th>
                    <th className={cx(s.th, s.thRight)}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBookings.map((b) => (
                    <tr key={b.ref} className={s.row} onClick={() => onOpenBooking(b)}>
                      <td className={s.td}>{b.plate ? <Plate size={12}>{b.plate}</Plate> : <span className={s.italicMuted}>to follow</span>}</td>
                      <td className={s.td}>{b.guestName}</td>
                      <td className={s.td}>{fmtDate(b.startDay, timezone, "ccc d LLL")}</td>
                      <td className={s.td}>{fmtDate(b.endDay, timezone, "ccc d LLL")}</td>
                      <td className={cx(s.td, s.mono, s.muted)}>{b.voucherId ?? "-"}</td>
                      <td className={s.td}>
                        <div className={s.statusCell}>
                          <StatusBadge status={b.status} />
                          {statusNote(b) && <span className={s.statusNote}>{statusNote(b)}</span>}
                        </div>
                      </td>
                      <td className={cx(s.td, s.tdRight)}>
                        <BookingRowAction
                          booking={b}
                          onTakePayment={onTakePayment}
                          onAssign={onAssign}
                          onValidate={onValidate}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {shouldShowBookingsToggle ? (
        <div className={s.showMoreWrap}>
          <button
            type="button"
            className={ui.ghostBtn}
            onClick={() => setShowAllBookings((value) => !value)}
          >
            {showAllBookings ? "Show less" : "Show more"}
          </button>
        </div>
      ) : null}
    </Panel>
  );
}
